/**
 * Statistics (F3): attendance (trend/average/growth), meetings,
 * members by status (baptized/serving) and finance summary — all
 * aggregation with PURE logic from `@agape/shared` over what sync already
 * authorized (finance summary only exists if account syncs
 * finances: data presence IS permission). Custom SVG charts,
 * theme-aware (currentColor/var(--primary)) and without forced animations
 * (transitions only under `motion-safe`).
 */
import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import {
  attendanceOverview,
  attendanceSeries,
  financeSummary,
  formatMoney,
  getAttendanceSessionsQuery,
  getChurchesQuery,
  getFinanceRecordsQuery,
  getGroupsQuery,
  getMeetingsQuery,
  getMembershipsQuery,
  membershipBreakdown,
  queryKeys,
  type StatsGranularity,
} from "@agape/shared";
import type {
  AttendanceSessionRecord,
  ChurchGroupRecord,
  ChurchRecord,
  FinanceRecordRecord,
  MeetingRecord,
  MembershipRecord,
} from "@agape/powersync";
import { TrendingDown, TrendingUp } from "@fleet/icons";

import { useI18n } from "@/providers/i18n-provider";
import { Card, CardContent } from "@fleet/ui/card";
import { PageSubHeader } from "@/components/ui/page-subheader";
import { Select } from "@/components/ui/select";
import { useDomainQuery } from "../use-domain";

/** Minimalist SVG bars: height relative to series maximum. */
function BarChart(props: {
  points: { label: string; value: number }[];
  testId?: string;
  ariaLabel: string;
}) {
  const max = () => Math.max(1, ...props.points.map((p) => p.value));
  const width = () => Math.max(1, props.points.length) * 34;
  return (
    <svg
      viewBox={`0 0 ${width()} 120`}
      class="h-32 w-full max-w-full"
      role="img"
      aria-label={props.ariaLabel}
      data-testid={props.testId}
      preserveAspectRatio="xMidYMid meet"
    >
      <For each={props.points}>
        {(p, i) => {
          const h = () => Math.max(2, Math.round((p.value / max()) * 88));
          return (
            <g>
              <title>{`${p.label}: ${p.value}`}</title>
              <rect
                x={i() * 34 + 5}
                y={100 - h()}
                width={24}
                height={h()}
                rx={4}
                class="fill-primary/80 motion-safe:transition-all"
                data-testid="stats-bar"
                data-period={p.label}
                data-value={p.value}
              />
              <text
                x={i() * 34 + 17}
                y={114}
                text-anchor="middle"
                class="fill-current text-muted-foreground"
                font-size="8"
              >
                {p.label.slice(-5)}
              </text>
            </g>
          );
        }}
      </For>
    </svg>
  );
}

function StatCard(props: { label: string; value: string; testId?: string }) {
  return (
    <Card>
      <CardContent class="py-3">
        <p class="text-xs uppercase tracking-wider text-muted-foreground">{props.label}</p>
        <p class="text-lg font-bold tabular-nums" data-testid={props.testId}>
          {props.value}
        </p>
      </CardContent>
    </Card>
  );
}

export function StatsPage() {
  const { t } = useI18n();

  const sessions = useDomainQuery<AttendanceSessionRecord>(
    (uid) => queryKeys.domain.attendanceSessions(uid),
    (db) => getAttendanceSessionsQuery(db),
  );
  const memberships = useDomainQuery<MembershipRecord>(
    (uid) => queryKeys.domain.memberships(uid),
    (db) => getMembershipsQuery(db),
  );
  const meetings = useDomainQuery<MeetingRecord>(
    (uid) => queryKeys.domain.meetings(uid),
    (db) => getMeetingsQuery(db),
  );
  const finances = useDomainQuery<FinanceRecordRecord>(
    (uid) => queryKeys.domain.financeRecords(uid),
    (db) => getFinanceRecordsQuery(db),
  );
  const churches = useDomainQuery<ChurchRecord>(
    (uid) => queryKeys.domain.churches(uid),
    (db) => getChurchesQuery(db),
  );
  const groups = useDomainQuery<ChurchGroupRecord>(
    (uid) => queryKeys.domain.groups(uid),
    (db) => getGroupsQuery(db),
  );

  const [churchId, setChurchId] = createSignal("");
  const [groupId, setGroupId] = createSignal("");
  const [granularity, setGranularity] = createSignal<StatsGranularity>("month");

  const activeChurch = () => churchId() || (churches.data ?? [])[0]?.id || "";
  const churchGroups = createMemo(() =>
    (groups.data ?? []).filter((g) => g.church_id === activeChurch()),
  );

  // ── Asistencia ────────────────────────────────────────────────────────────
  const series = createMemo(() =>
    attendanceSeries(
      (sessions.data ?? []).map((s) => ({
        id: s.id,
        headcount: s.headcount,
        occurrence_start: s.occurrence_start,
        church_id: s.church_id,
        group_id: s.group_id,
        meeting_id: s.meeting_id,
      })),
      granularity(),
      { churchId: activeChurch() || null, groupId: groupId() || null },
    ),
  );
  const overview = createMemo(() => attendanceOverview(series()));

  // ── Miembros ──────────────────────────────────────────────────────────────
  const members = createMemo(() =>
    membershipBreakdown(
      (memberships.data ?? []).map((m) => ({
        id: m.id,
        church_id: m.church_id,
        status: m.status,
        baptized: m.baptized,
        serves: m.serves,
        is_leader: m.is_leader,
        is_pastor: m.is_pastor,
        deleted_at: m.deleted_at,
      })),
      activeChurch() || null,
    ),
  );

  // ── Meetings (synchronized series of chosen scope) ──────────────────────
  const meetingCount = createMemo(
    () =>
      (meetings.data ?? []).filter(
        (m) =>
          (!activeChurch() || m.church_id === activeChurch()) &&
          (!groupId() || m.group_id === groupId()),
      ).length,
  );

  // ── Finances (only if sync delivers finances to this account) ────────────
  const records = createMemo(() =>
    (finances.data ?? []).map((r) => ({
      id: r.id,
      kind: r.kind,
      amount_minor: r.amount_minor,
      currency: r.currency,
      occurred_at: r.occurred_at,
      created_at: r.created_at,
      fund_id: r.fund_id,
      category_id: r.category_id,
      church_id: r.church_id,
    })),
  );
  const churchFinance = createMemo(() =>
    financeSummary(records(), { churchId: activeChurch() || null }),
  );
  const financeCurrency = () =>
    records().find((r) => r.church_id === activeChurch())?.currency ?? "EUR";

  const growthText = () => {
    const g = overview().growthPct;
    if (g === null) {
      return "—";
    }
    return `${g > 0 ? "+" : ""}${g.toFixed(1)}%`;
  };

  return (
    <div class="space-y-4" data-testid="stats-page">
      <PageSubHeader title={t(msg`Statistics`)} />
      <div class="flex flex-wrap items-end gap-3">
        <div class="min-w-44">
          <Select
            options={(churches.data ?? []).map((c) => ({ value: c.id, label: c.name ?? c.id }))}
            value={activeChurch()}
            onChange={(v) => {
              setChurchId(v);
              setGroupId("");
            }}
            aria-label={t(msg`Church`)}
            data-testid="stats-church-select"
          />
        </div>
        <div class="min-w-40">
          <Select
            options={[
              { value: "", label: t(msg`Whole church`) },
              ...churchGroups().map((g) => ({ value: g.id, label: g.name ?? g.id })),
            ]}
            value={groupId()}
            onChange={setGroupId}
            aria-label={t(msg`Group`)}
            data-testid="stats-group-select"
          />
        </div>
        <div class="min-w-32">
          <Select
            options={[
              { value: "month", label: t(msg`By month`) },
              { value: "week", label: t(msg`By week`) },
            ]}
            value={granularity()}
            onChange={(v) => setGranularity(v === "week" ? "week" : "month")}
            aria-label={t(msg`Granularity`)}
            data-testid="stats-granularity-select"
          />
        </div>
      </div>

      {/* Asistencia */}
      <Card>
        <CardContent class="space-y-3 py-4" data-testid="stats-attendance">
          <p class="text-xs font-bold uppercase tracking-wider opacity-60">{t(msg`Attendance`)}</p>
          <div class="grid grid-cols-3 gap-3">
            <StatCard
              label={t(msg`Total`)}
              value={String(overview().total)}
              testId="stats-attendance-total"
            />
            <StatCard
              label={t(msg`Average per session`)}
              value={overview().average.toFixed(1)}
              testId="stats-attendance-average"
            />
            <Card>
              <CardContent class="py-3">
                <p class="text-xs uppercase tracking-wider text-muted-foreground">
                  {t(msg`Growth`)}
                </p>
                <p
                  class="flex items-center gap-1 text-lg font-bold tabular-nums"
                  data-testid="stats-attendance-growth"
                >
                  <Show
                    when={(overview().growthPct ?? 0) >= 0}
                    fallback={<TrendingDown size={18} class="text-destructive" />}
                  >
                    <TrendingUp size={18} class="text-primary" />
                  </Show>
                  {growthText()}
                </p>
              </CardContent>
            </Card>
          </div>
          <Show
            when={series().length > 0}
            fallback={
              <p class="text-sm text-muted-foreground" data-testid="stats-attendance-empty">
                {t(msg`No attendance sessions synced for this scope yet.`)}
              </p>
            }
          >
            <BarChart
              points={series()
                .slice(-12)
                .map((p) => ({ label: p.period, value: p.total }))}
              ariaLabel={t(msg`Attendance trend`)}
              testId="stats-attendance-chart"
            />
          </Show>
        </CardContent>
      </Card>

      {/* Miembros + reuniones */}
      <Card>
        <CardContent class="space-y-3 py-4" data-testid="stats-members">
          <p class="text-xs font-bold uppercase tracking-wider opacity-60">{t(msg`Members`)}</p>
          <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard
              label={t(msg`Members`)}
              value={String(members().total)}
              testId="stats-members-total"
            />
            <StatCard
              label={t(msg`Baptized`)}
              value={String(members().baptized)}
              testId="stats-members-baptized"
            />
            <StatCard
              label={t(msg`Serving`)}
              value={String(members().serving)}
              testId="stats-members-serving"
            />
            <StatCard
              label={t(msg`Meetings`)}
              value={String(meetingCount())}
              testId="stats-meetings-count"
            />
          </div>
          <Show when={members().byStatus.length > 0}>
            <div class="space-y-1" data-testid="stats-members-by-status">
              <For each={members().byStatus}>
                {(s) => {
                  const pct = () => Math.round((s.count / Math.max(1, members().total)) * 100);
                  return (
                    <div class="flex items-center gap-2 text-sm">
                      <span class="w-24 shrink-0 truncate capitalize">{s.status}</span>
                      <div class="h-2 flex-1 overflow-hidden rounded-full bg-accent">
                        <div
                          class="h-full rounded-full bg-primary/80 motion-safe:transition-all"
                          style={{ width: `${pct()}%` }}
                        />
                      </div>
                      <span class="w-8 shrink-0 text-right tabular-nums">{s.count}</span>
                    </div>
                  );
                }}
              </For>
            </div>
          </Show>
        </CardContent>
      </Card>

      {/* Finanzas (la presencia del dato ES el permiso) */}
      <Show when={records().some((r) => r.church_id === activeChurch())}>
        <Card>
          <CardContent class="space-y-3 py-4" data-testid="stats-finance">
            <p class="text-xs font-bold uppercase tracking-wider opacity-60">{t(msg`Finances`)}</p>
            <div class="grid grid-cols-3 gap-3">
              <StatCard
                label={t(msg`Income`)}
                value={formatMoney(churchFinance().incomeMinor, financeCurrency())}
                testId="stats-finance-income"
              />
              <StatCard
                label={t(msg`Expenses`)}
                value={formatMoney(churchFinance().expenseMinor, financeCurrency())}
                testId="stats-finance-expenses"
              />
              <StatCard
                label={t(msg`Net`)}
                value={formatMoney(churchFinance().netMinor, financeCurrency())}
                testId="stats-finance-net"
              />
            </div>
            <Show when={churchFinance().byMonth.length > 0}>
              <BarChart
                points={churchFinance()
                  .byMonth.slice(-12)
                  .map((b) => ({ label: b.key, value: Math.max(0, b.netMinor) }))}
                ariaLabel={t(msg`Net by month`)}
                testId="stats-finance-chart"
              />
            </Show>
          </CardContent>
        </Card>
      </Show>
    </div>
  );
}
